import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { FileText, ChevronRight } from 'lucide-react';
import { CONSENT_ITEMS_BY_COURSE, COURSE_NAME } from './config';

const headingFont = { fontFamily: "'Outfit', sans-serif" };

/**
 * LGPD-compliant consent modal. Items are course-specific and rendered dynamically
 * from CONSENT_ITEMS_BY_COURSE. All items must be checked before user can proceed.
 *
 * Uses shadcn/ui Checkbox (Radix-based) with proper ARIA semantics and full
 * keyboard support (Space toggles, Tab navigates). The Checkbox is wired to its
 * Label via htmlFor/id so clicking the label area also toggles the box, as
 * required by native HTML <label> semantics.
 */
export function ConsentModal({ consentModal, consentChecked, onToggleItem, onConfirm, onClose }) {
  const isOpen = consentModal !== null;
  const items = consentModal ? CONSENT_ITEMS_BY_COURSE[consentModal] || [] : [];
  const allChecked = items.length > 0 && items.every((item) => consentChecked[item.key]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="bg-slate-900 border border-slate-700 text-white w-[calc(100vw-2rem)] max-w-xl max-h-[90vh] overflow-y-auto"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        <DialogHeader>
          <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-center justify-center mb-4">
            <FileText className="w-6 h-6 text-blue-400" />
          </div>
          <DialogTitle className="text-xl font-bold text-white" style={headingFont}>
            Termo de Ciência e Consentimento — LGPD
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm leading-relaxed mt-1">
            Para concluir sua matrícula no <strong className="text-slate-200">{COURSE_NAME[consentModal] || ''}</strong>, leia e confirme os termos abaixo, em cumprimento à Lei Geral de Proteção de Dados (Lei nº 13.709/2018).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-4">
          {items.map((item) => {
            const checkboxId = `consent-${item.key}`;
            const isChecked = !!consentChecked[item.key];
            return (
              <div
                key={item.key}
                className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 hover:border-blue-500/30 transition-colors"
              >
                <Checkbox
                  id={checkboxId}
                  data-testid={`consent-${item.key}-checkbox`}
                  checked={isChecked}
                  onCheckedChange={() => onToggleItem(item.key)}
                  className="mt-1 border-slate-500 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 data-[state=checked]:text-white"
                />
                <label
                  htmlFor={checkboxId}
                  data-testid={`consent-${item.key}-label`}
                  className="flex-1 cursor-pointer select-none"
                >
                  <p className="text-white text-sm font-semibold mb-1">{item.title}</p>
                  <p className="text-slate-400 text-xs leading-relaxed">{item.text}</p>
                </label>
              </div>
            );
          })}
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 mb-4">
          <p className="text-slate-500 text-xs leading-relaxed">
            Ao prosseguir, você confirma que leu, compreendeu e concorda com todos os termos acima. Este consentimento é condição para a efetivação da matrícula no curso, nos termos da LGPD.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            data-testid="consent-confirm-btn"
            onClick={onConfirm}
            disabled={!allChecked}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl text-base disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Concordo e prosseguir para pagamento
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
          <Button
            data-testid="consent-cancel-btn"
            variant="ghost"
            onClick={onClose}
            className="w-full text-slate-400 hover:text-white hover:bg-slate-800 text-sm py-3"
          >
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
